















import { Component, Prop, Vue } from 'vue-property-decorator';
import { GraphsApi } from '@/api';
import moment from 'moment';
import DatePicker from '@/components/form pickers/DatePicker.vue';
import { DateRange } from '@/models/date-range';
import { LineSeries } from '@amcharts/amcharts5/.internal/charts/xy/series/LineSeries';
import { Color, Legend, percent, Root, Scrollbar, Tooltip } from '@amcharts/amcharts5';
import { DateAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/DateAxis';
import { AxisRenderer } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRenderer';
import { ApiUtils } from '@/common/ApiUtils';
import { XYChart } from '@amcharts/amcharts5/.internal/charts/xy/XYChart';
import { ValueAxis } from '@amcharts/amcharts5/.internal/charts/xy/axes/ValueAxis';
import { XYCursor } from '@amcharts/amcharts5/.internal/charts/xy/XYCursor';
import { AxisRendererX } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererX';
import { AxisRendererY } from '@amcharts/amcharts5/.internal/charts/xy/axes/AxisRendererY';

@Component({
	components: { DatePicker },
})
export default class EntityChargingBehaviourChart extends Vue {
	@Prop({ required: true })
	public entityId!: number;

	@Prop({ required: true })
	public entityType!: string;

	public datePicker?: DatePicker | null = null;

	public errorMessage: string | null = null;
	public loading: boolean = true;
	public graphData: any[] | null = null;

	private graphApi: GraphsApi = new GraphsApi();

	private chargePercentSeries: LineSeries | null = null;
	private solarVoltageSeries: LineSeries | null = null;
	private cellStrengthSeries: LineSeries | null = null;

	private xAxis: DateAxis<AxisRenderer> | null = null;

	private chargePercentYAxis: ValueAxis<AxisRenderer> | null = null;
	private solarVoltageYAxis: ValueAxis<AxisRenderer> | null = null;
	private cellStrengthYAxis: ValueAxis<AxisRenderer> | null = null;

	private startDate: moment.Moment = moment()
		.startOf('day')
		.subtract(5, 'days');
	private endDate: moment.Moment = moment()
		.startOf('day')
		.add(1, 'days');

	public async created(): Promise<void> {
		if (this.entityId == null || this.entityType == null) {
			this.errorMessage = 'Invalid entity provided';
			return;
		}
		await this.fetchGraphData(this.entityId, this.entityType);
		this.createChart(this.graphData!);

		this.datePicker = <DatePicker>this.$refs.datePicker;
		this.datePicker?.onDateRangeChanged(<DateRange>{
			start: this.startDate.format('DD-MM-YYYY'),
			end: this.endDate.format('DD-MM-YYYY'),
		});
	}

	public async onDateRangeChanged(newDateRange: Omit<DateRange, 'shortcut'>): Promise<void> {
		this.startDate = moment(newDateRange.start, 'DD-MM-YYYY');
		this.endDate = moment(newDateRange.end, 'DD-MM-YYYY');

		await this.fetchGraphData(this.entityId, this.entityType);

		this.chargePercentSeries?.data.setAll(this.formatGraphData(this.graphData!, 'soc_pct'));
		this.solarVoltageSeries?.data.setAll(this.formatGraphData(this.graphData!, 'solar_mV'));
		this.cellStrengthSeries?.data.setAll(this.formatGraphData(this.graphData!, 'cell_strength'));

		this.setYAxisRanges();

		this.xAxis?.setAll({
			min: this.startDate.valueOf(),
			max: Math.min(this.endDate.valueOf(), Date.now()),
		});
	}

	public getRangeMax(variable: string): number {
		return (
			Math.max(...(this.graphData?.map(x => x[variable] ?? null)?.filter(x => x != null) ?? [])) *
			1.1
		);
	}

	public setYAxisRanges() {
		this.solarVoltageYAxis?.setAll({
			min: 0,
			max: this.getRangeMax('solar_mV'),
		});
		this.chargePercentYAxis?.setAll({
			min: 0,
			max: this.getRangeMax('soc_pct'),
		});
		this.cellStrengthYAxis?.setAll({
			min: 0,
			max: this.getRangeMax('cell_strength'),
		});
	}

	private async fetchGraphData(entityId: number, entityType: string): Promise<void> {
		try {
			const graphDataResponse = await this.graphApi.graph(
				entityType,
				entityId,
				this.startDate.format('X'),
				this.endDate.format('X'),
				'power,cell-strength',
			);
			this.graphData =
				(graphDataResponse.data.data?.['power'] ?? []).map((x: any) => {
					return { ...x.value, timestamp: x.timestamp };
				}) ?? [];

			const cellStrengthData = (graphDataResponse.data.data?.['cell-strength'] ?? []).map(
				(x: any) => {
					return { cell_strength: x.rating, timestamp: x.timestamp };
				},
			);

			this.graphData = [...this.graphData, ...cellStrengthData].sort((a, b) =>
				a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
			);
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load graph data');
			this.errorMessage = errorResponse.errorMessage;
			if (this.errorMessage === 'Resource not found') this.errorMessage = null;
			this.graphData = [];
		} finally {
			this.loading = false;
		}
	}

	private createChart(graphData: any[]): void {
		const root: Root = Root.new('fuelTankFaultChart');
		const chart: XYChart = XYChart.new(root, {});

		const {
			cursor,
			xAxis,
			chargePercentYAxis,
			solarVoltageYAxis,
			cellStrengthYAxis,
			chargePercentSeries,
			solarVoltageSeries,
			cellStrengthSeries,
			legend,
		} = this.createChartFeatures(root);
		this.chargePercentSeries = chargePercentSeries;
		this.solarVoltageSeries = solarVoltageSeries;
		this.cellStrengthSeries = cellStrengthSeries;

		this.chargePercentYAxis = chargePercentYAxis;
		this.solarVoltageYAxis = solarVoltageYAxis;
		this.cellStrengthYAxis = cellStrengthYAxis;

		this.xAxis = xAxis;
		chart.set('cursor', cursor);
		chart.xAxes.push(xAxis);

		chart.yAxes.push(chargePercentYAxis);
		chart.yAxes.push(solarVoltageYAxis);
		chart.yAxes.push(cellStrengthYAxis);

		chart.series.push(chargePercentSeries);
		chart.series.push(solarVoltageSeries);
		chart.series.push(cellStrengthSeries);

		let scrollbarX = Scrollbar.new(root, { orientation: 'horizontal' });
		chart.set('scrollbarX', scrollbarX);

		chart.bottomAxesContainer.children.push(legend);

		chargePercentSeries.data.setAll(this.formatGraphData(graphData, 'soc_pct'));
		solarVoltageSeries.data.setAll(this.formatGraphData(graphData, 'solar_mV'));
		cellStrengthSeries.data.setAll(this.formatGraphData(graphData, 'cell_strength'));

		legend.data.setAll(chart.series.values);
		root.container.children.push(chart);

		chargePercentSeries.appear(1000);
		solarVoltageSeries.appear(1000);
		cellStrengthSeries.appear(1000);

		chart.appear(1000, 100);
	}

	private formatGraphData(
		graphData: any[],
		field: string,
		labelField?: string,
		min?: number,
		max?: number,
	): {
		value: number | undefined;
		date: number;
	}[] {
		return <{ value: number | undefined; date: number }[]>graphData
				?.filter(
					(item: any) => min == null || max == null || (item[field] >= min && item[field] <= max),
				)
				.map((item: any, index: number) => {
					if (field === 'soc_pct' && item[field] === 1000) return null;

					let out: any = {
						value: item[field],
						date: item.timestamp,
					};

					if (labelField) {
						const diffToPrev = index === 0 || graphData[index - 1][labelField] !== item[labelField];
						if (diffToPrev) out.label = item[labelField];
						else return null;
					}
					return out;
				})
				?.filter(x => x != null) ?? [];
	}

	private createChartFeatures(
		root: Root,
	): {
		cursor: XYCursor;
		xAxis: DateAxis<AxisRenderer>;
		chargePercentYAxis: ValueAxis<AxisRenderer>;
		solarVoltageYAxis: ValueAxis<AxisRenderer>;
		cellStrengthYAxis: ValueAxis<AxisRenderer>;
		chargePercentSeries: LineSeries;
		solarVoltageSeries: LineSeries;
		cellStrengthSeries: LineSeries;
		legend: Legend;
	} {
		const xAxis: DateAxis<AxisRenderer> = DateAxis.new(root, {
			baseInterval: { timeUnit: 'minute', count: 15 },
			renderer: AxisRendererX.new(root, {}),
			tooltip: Tooltip.new(root, {}),
			min: this.startDate.toDate().getTime(),
			max: Math.min(this.endDate.toDate().getTime(), Date.now()),
		});

		const chargePercentYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
		});

		const solarVoltageYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
		});

		const cellStrengthYAxis: ValueAxis<AxisRenderer> = ValueAxis.new(root, {
			renderer: AxisRendererY.new(root, {
				opposite: true,
			}),
		});

		const cursor: XYCursor = XYCursor.new(root, { behavior: 'zoomX' });

		const chargePercentSeries: LineSeries = LineSeries.new(root, {
			name: 'Charge (%)',
			xAxis,
			yAxis: chargePercentYAxis,
			tooltip: Tooltip.new(root, {
				labelText: 'Charge: {valueY}%',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0xf6861f),
			stroke: Color.fromHex(0xf6861f),
		});
		chargePercentSeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const solarVoltageSeries: LineSeries = LineSeries.new(root, {
			name: 'Solar Voltage (mV)',
			xAxis,
			yAxis: solarVoltageYAxis,
			tooltip: Tooltip.new(root, {
				labelText: '{valueY}mV',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0x769edf),
			stroke: Color.fromHex(0x769edf),
		});
		solarVoltageSeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const cellStrengthSeries: LineSeries = LineSeries.new(root, {
			name: 'Cell Strength',
			xAxis,
			yAxis: cellStrengthYAxis,
			tooltip: Tooltip.new(root, {
				labelText: '{valueY}',
			}),
			showTooltipOn: 'always',
			valueXField: 'date',
			valueYField: 'value',
			connect: true,
			snapTooltip: true,
			fill: Color.fromHex(0x69c97e),
			stroke: Color.fromHex(0x69c97e),
		});
		cellStrengthSeries.strokes.template.setAll({
			strokeWidth: 2,
		});

		const chargePercentYAxisRenderer = chargePercentYAxis.get('renderer');
		chargePercentYAxisRenderer.grid.template.setAll({
			forceHidden: true,
		});
		chargePercentYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: chargePercentSeries.get('fill'),
		});

		const solarVoltageYAxisRenderer = solarVoltageYAxis.get('renderer');
		solarVoltageYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: solarVoltageSeries.get('fill'),
		});

		const cellStrengthYAxisRenderer = cellStrengthYAxis.get('renderer');
		cellStrengthYAxisRenderer.labels.template.setAll({
			fontFamily: 'sofia-pro-soft',
			fill: cellStrengthSeries.get('fill'),
		});

		const legend: Legend = Legend.new(root, {
			x: percent(50),
			centerX: percent(50),
		});
		return {
			xAxis,
			chargePercentYAxis,
			solarVoltageYAxis,
			cellStrengthYAxis,
			cursor,
			chargePercentSeries,
			solarVoltageSeries,
			cellStrengthSeries,
			legend,
		};
	}
}
